import { useCallback, useEffect, useState, type ReactNode } from 'react';
import dayjs from 'dayjs';
import { apiService } from '../../services/api';
import { useSubscription } from '../../hooks/useSubscription';
import { hasAcceptedCurrentTerms } from '../../legal/terms';
import ReleaseNotesModal from './ReleaseNotesModal';
import { ReleaseNotesContextProvider } from './releaseNotesContext';
import { useReleaseNotes } from './useReleaseNotes';
import type { ReleaseNotesAdapter } from './types';

function profileOf(response: { data?: unknown } | null | undefined) {
  const data = response?.data as { profile?: Record<string, unknown> } | Record<string, unknown> | undefined;
  if (!data) return null;
  return ((data as { profile?: Record<string, unknown> }).profile || data) as Record<string, any>;
}

function useStageNoteReleaseNotesAdapter(): ReleaseNotesAdapter {
  const { subscription, loading } = useSubscription();
  const [profile, setProfile] = useState<Record<string, any> | null>(null);
  const [profileReady, setProfileReady] = useState(false);

  useEffect(() => {
    if (profile?.country !== 'NL') return;
    import('dayjs/locale/en-gb').then(() => dayjs.locale('en-gb'));
  }, [profile?.country]);

  useEffect(() => {
    let cancelled = false;
    apiService.getProfile()
      .then((response) => { if (!cancelled) setProfile(profileOf(response)); })
      .catch(() => { if (!cancelled) setProfile(null); })
      .finally(() => { if (!cancelled) setProfileReady(true); });
    return () => { cancelled = true; };
  }, []);

  const features: Record<string, boolean> = {};
  for (const id of subscription?.subscription?.enabledFeatures || []) features[id] = true;

  const setupComplete = Boolean(profile?.isProfileComplete || profile?.setupComplete);
  const termsAccepted = hasAcceptedCurrentTerms(profile);

  const loadLastSeen = useCallback(async () => {
    const response = await apiService.getProfile();
    const current = profileOf(response);
    return current?.releaseNotes?.lastSeenVersion || null;
  }, []);

  const saveLastSeen = useCallback(async (version: string) => {
    await apiService.updateProfile({
      releaseNotes: { lastSeenVersion: version, seenAt: new Date().toISOString() },
    });
  }, []);

  return {
    appName: 'StageNote',
    ready: !loading && profileReady && termsAccepted && setupComplete,
    features,
    suppressAutoOpen: false,
    loadLastSeen,
    saveLastSeen,
  };
}

export function StageNoteReleaseNotes({ children }: { children: ReactNode }) {
  const adapter = useStageNoteReleaseNotesAdapter();
  const notes = useReleaseNotes(adapter);
  return (
    <ReleaseNotesContextProvider value={{ openHistory: notes.openHistory }}>
      {children}
      <ReleaseNotesModal
        open={notes.open}
        appName={notes.appName}
        mode={notes.mode}
        releases={notes.releases}
        onClose={notes.dismiss}
      />
    </ReleaseNotesContextProvider>
  );
}
